import { useTranslation } from 'react-i18next'

import {
  publishRentalPortalListingPortals,
  updateRentalPortalListing,
} from '../../../../../../api'
import { useNotification } from '../../../../../../hooks'
import {
  getHasListingPublishSection,
  getListingPortalsPublishPayload,
  getListingTimingErrorText,
  setListingFormSubmitError,
} from '../../RentalPortalListingsForm.utils'

import {
  getListingFormScheduleValues,
  getListingPublishErrorText,
  getListingPublishFailedPortals,
  getListingPublishFailedText,
  getListingPublishFailureText,
  getListingPublishNotificationTitleKey,
  getListingPublishOutcomes,
  getListingPublishSucceededText,
} from './useRentalPortalListingsFormPublish.utils'

export function useRentalPortalListingsFormPublish({
  getRecordVersion,
  listingId,
  onChangeVersion,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  async function applyTiming({ hasPortalsToPublish, record, values }) {
    if (!getHasListingPublishSection({ listingId })) {
      return record
    }

    const { goLive, hasPersistedSchedule, scheduledDeactivate } =
      getListingFormScheduleValues({ hasPortalsToPublish, record, values })

    if (!goLive && !scheduledDeactivate && !hasPersistedSchedule) {
      return record
    }

    const { data } = await updateRentalPortalListing({
      data: {
        scheduled_deactivate: scheduledDeactivate,
        scheduled_go_live: goLive,
      },
      id: record.id,
      version: getRecordVersion(),
    })

    onChangeVersion(data.version)

    return data
  }

  async function publishToPortals({ record, values }) {
    const { data } = await publishRentalPortalListingPortals({
      data: getListingPortalsPublishPayload(values),
      id: record.id,
    })

    return data.publications
  }

  async function saveListingTiming({
    formikHelpers,
    hasPortalsToPublish,
    record,
    values,
  }) {
    const { setErrors, setSubmitting } = formikHelpers

    try {
      return await applyTiming({ hasPortalsToPublish, record, values })
    } catch (error) {
      setListingFormSubmitError({
        errorText: getListingTimingErrorText({ error, t }),
        setErrors,
        setSubmitting,
      })

      return null
    }
  }

  async function publishListing({ formikHelpers, record, values }) {
    const { setErrors, setSubmitting } = formikHelpers

    try {
      const { failed, succeeded } = getListingPublishOutcomes(
        await publishToPortals({ record, values })
      )

      if (succeeded.length === 0) {
        setListingFormSubmitError({
          errorText: getListingPublishFailureText({
            publications: failed,
            t,
          }),
          setErrors,
          setSubmitting,
        })

        return false
      }

      handleAddSuccessNotification({
        text: getListingPublishSucceededText({ publications: succeeded, t }),
        title: t(getListingPublishNotificationTitleKey(values)),
      })

      if (failed.length > 0) {
        handleAddErrorNotification({
          text: getListingPublishFailedText({ publications: failed, t }),
          title: t(
            'RentalPortal.ListingFormPublishPortalsFailedNotificationKey',
            { portals: getListingPublishFailedPortals(failed) }
          ),
        })
      }

      return true
    } catch (error) {
      setListingFormSubmitError({
        errorText: getListingPublishErrorText({ error, t }),
        setErrors,
        setSubmitting,
      })

      return false
    }
  }

  return { publishListing, saveListingTiming }
}
